import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';
import { colors, FONT } from '../../theme';
import { fetchFileObjectURL } from '../../services/fileFetch';

export default function VoiceMessage({ fileId, fileKey, duration, isMine }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const audioRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    // Load with auth; a sealed voice note is decrypted with the key from
    // the envelope, an old plain one comes back as-is. (r.ok is checked
    // inside — a 404 body once became the audio source: the message looked
    // loaded, the button worked, and it played nothing.)
    fetchFileObjectURL(fileId, fileKey, 'audio/webm')
      .then(url => {
        audio.src = url;
        setLoaded(true);
      })
      .catch((err) => {
        console.warn('[VoiceMessage] load failed:', err.message);
        setLoadError(/404/.test(err.message) ? 'Файл удалён' : 'Не удалось загрузить');
      });

    audio.addEventListener('ended', () => { setPlaying(false); setProgress(0); });

    return () => {
      cancelAnimationFrame(animRef.current);
      audio.pause();
      if (audio.src) URL.revokeObjectURL(audio.src);
    };
  }, [fileId]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio || !loaded) return;

    if (playing) {
      audio.pause();
      cancelAnimationFrame(animRef.current);
    } else {
      audio.play();
      const tick = () => {
        if (audio.duration) setProgress(audio.currentTime / audio.duration);
        animRef.current = requestAnimationFrame(tick);
      };
      tick();
    }
    setPlaying(!playing);
  };

  const fmtDur = (s) => {
    if (!s) return '0:00';
    const sec = Math.round(s);
    return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
  };

  const barColor = isMine ? '#6e8ad0' : colors.gray400;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 160 }}>
      <button onClick={toggle} style={{
        width: 36, height: 36, borderRadius: '50%',
        background: barColor, border: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: loadError ? 'default' : 'pointer', flexShrink: 0, opacity: loaded ? 1 : 0.4,
      }}>
        {playing ? <Pause size={16} color="#15141a" /> : <Play size={16} color="#15141a" style={{ marginLeft: 2 }} />}
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Progress bar */}
        <div style={{ height: 4, borderRadius: 2, background: isMine ? 'rgba(91,141,239,0.25)' : colors.gray200, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 2, background: barColor, width: `${progress * 100}%`, transition: 'width 0.1s' }} />
        </div>
        <div style={{ fontSize: 11, color: isMine ? '#6e8ad0' : colors.gray400, marginTop: 3, fontFamily: FONT }}>
          {fmtDur(duration)}
        </div>
      </div>
    </div>
  );
}
